import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, TrendingUp, Lightbulb, Clock, Zap, ZapOff } from "lucide-react";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface DashboardInsightsProps {
  insights: string;
  suggestions: string[];
  analyzedAt: string;
  isAnalyzing: boolean;
  onRefresh: () => void;
}

export const DashboardInsights = ({
  insights,
  suggestions,
  analyzedAt,
  isAnalyzing,
  onRefresh,
}: DashboardInsightsProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const saved = localStorage.getItem('ai-insights-animations');
    return saved !== 'false';
  });

  useEffect(() => {
    localStorage.setItem('ai-insights-animations', String(animationsEnabled));
  }, [animationsEnabled]);

  // Charger l'état d'expansion depuis localStorage
  useEffect(() => {
    const stored = localStorage.getItem('dashboard_insights_expanded');
    if (stored !== null) {
      setIsExpanded(stored === 'true');
    }
  }, []);

  // Sauvegarder l'état d'expansion
  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem('dashboard_insights_expanded', String(newState));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getTimeSinceAnalysis = () => {
    const date = new Date(analyzedAt);
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `Il y a ${hours}h`;
  };

  return (
    <Card className={`relative overflow-hidden bg-gradient-to-br from-[hsl(var(--ai-primary)/0.05)] via-[hsl(var(--ai-secondary)/0.05)] to-[hsl(var(--ai-accent)/0.05)] border-2 border-[hsl(var(--ai-primary)/0.2)]`}>
      {animationsEnabled && (
        <div className="absolute inset-0 ai-shimmer opacity-30 pointer-events-none" />
      )}
      
      <div className="relative p-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              {animationsEnabled && (
                <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] rounded-full blur-md opacity-50 ai-pulse" />
              )}
              <div className="relative bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] p-2.5 rounded-full">
                <Sparkles className="h-5 w-5 text-white ai-float" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] bg-clip-text text-transparent">
                Insights IA
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{getTimeSinceAnalysis()}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAnimationsEnabled(!animationsEnabled)}
              className="h-9 w-9 p-0 hover:bg-[hsl(var(--ai-primary)/0.1)] transition-all duration-300"
              title={animationsEnabled ? "Désactiver les animations" : "Activer les animations"}
            >
              {animationsEnabled ? (
                <Zap className="h-4 w-4 text-[hsl(var(--ai-primary))]" />
              ) : (
                <ZapOff className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={isAnalyzing}
              className="h-9 px-3 hover:bg-[hsl(var(--ai-primary)/0.1)] transition-all duration-300 group"
            >
              <RefreshCw className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
              <span className="ml-2 text-sm">Actualiser</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleExpanded}
              className="h-9 w-9 p-0 hover:bg-[hsl(var(--ai-primary)/0.1)]"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="relative px-4 pb-4 space-y-4">
          {isAnalyzing ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] rounded-full blur-xl opacity-30 ai-pulse" />
                <RefreshCw className="relative h-8 w-8 animate-spin text-[hsl(var(--ai-primary))]" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">Analyse en cours...</span>
              <div className="w-48 h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] animate-pulse" style={{ width: '60%' }} />
              </div>
            </div>
          ) : (
            <>
              {insights && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                    </div>
                    <h4 className="text-sm font-bold">Analyse générale</h4>
                  </div>
                  <div className="p-4 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50">
                    <p 
                      className="text-sm text-foreground/90 leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(
                          (insights || '')
                            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.+?)\*/g, '<em>$1</em>'),
                          { ALLOWED_TAGS: ['strong', 'em', 'p', 'br'] }
                        )
                      }}
                    />
                  </div>
                </div>
              )}

              {Array.isArray(suggestions) && suggestions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-500/10 rounded-lg">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                    </div>
                    <h4 className="text-sm font-bold">Recommandations</h4>
                    <Badge variant="secondary" className="ml-auto text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                      {suggestions.length}
                    </Badge>
                  </div>
                  <ul className="space-y-2">
                    {suggestions.filter(s => s && typeof s === 'string').map((suggestion, index) => (
                      <li 
                        key={index} 
                        className={`group p-3 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50 hover:border-amber-500/30 hover:bg-amber-500/5 transition-all duration-300 ${animationsEnabled ? 'hover:scale-[1.02]' : ''}`}
                        style={animationsEnabled ? { animationDelay: `${index * 100}ms` } : undefined}
                      >
                        <div className="flex gap-3">
                          <Sparkles className={`h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5 ${animationsEnabled ? 'group-hover:scale-110' : ''} transition-transform`} />
                          <span 
                            className="text-sm text-foreground/90"
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(
                                (suggestion || '')
                                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/\*(.+?)\*/g, '<em>$1</em>'),
                                { ALLOWED_TAGS: ['strong', 'em', 'p', 'br'] }
                              )
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className="pt-4 mt-4 border-t border-border/50">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              <span>Propulsé par IA avancée</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
